/**
 * Pure mapping between the appearance store's wire shape (camelCase keys, lowercase enum
 * strings) and the VisionAccount proto messages (enum-typed). Kept pure and isolated so it
 * is independently testable and the store stays unaware of the transport's enum typing.
 */

import {
  CardSizePreset,
  AspectRatio,
  ThumbnailScale,
} from '../gen/vision/account/v1/vision_preferences_pb';

const CARD_SIZE_TO_PROTO = {
  S: CardSizePreset.SMALL,
  M: CardSizePreset.MEDIUM,
  L: CardSizePreset.LARGE,
};
const ASPECT_RATIO_TO_PROTO = {
  horizontal: AspectRatio.HORIZONTAL,
  square: AspectRatio.SQUARE,
  vertical: AspectRatio.VERTICAL,
};
const THUMBNAIL_SCALE_TO_PROTO = { fit: ThumbnailScale.FIT, fill: ThumbnailScale.FILL };

const CARD_SIZE_FROM_PROTO = invert(CARD_SIZE_TO_PROTO);
const ASPECT_RATIO_FROM_PROTO = invert(ASPECT_RATIO_TO_PROTO);
const THUMBNAIL_SCALE_FROM_PROTO = invert(THUMBNAIL_SCALE_TO_PROTO);

function invert(map) {
  return Object.fromEntries(Object.entries(map).map(([key, value]) => [value, key]));
}

function lookup(map, key, label) {
  const mapped = map[key];
  if (mapped === undefined) {
    throw new Error(`Unmappable ${label}: ${key}`);
  }
  return mapped;
}

export function boardToProto(board) {
  return {
    cardSizePreset: lookup(CARD_SIZE_TO_PROTO, board.cardSizePreset, 'cardSizePreset'),
    aspectRatio: lookup(ASPECT_RATIO_TO_PROTO, board.aspectRatio, 'aspectRatio'),
    thumbnailScale: lookup(THUMBNAIL_SCALE_TO_PROTO, board.thumbnailScale, 'thumbnailScale'),
  };
}

export function boardFromProto(settings) {
  return {
    cardSizePreset: lookup(CARD_SIZE_FROM_PROTO, settings.cardSizePreset, 'cardSizePreset'),
    aspectRatio: lookup(ASPECT_RATIO_FROM_PROTO, settings.aspectRatio, 'aspectRatio'),
    thumbnailScale: lookup(THUMBNAIL_SCALE_FROM_PROTO, settings.thumbnailScale, 'thumbnailScale'),
  };
}

export function prefsFromProto(preferences) {
  const prefs = {};
  if (preferences.appearanceArtboard) {
    prefs.appearance_artboard = boardFromProto(preferences.appearanceArtboard);
  }
  if (preferences.appearanceStoryboard) {
    prefs.appearance_storyboard = boardFromProto(preferences.appearanceStoryboard);
  }
  if (preferences.appearanceVisionboard) {
    prefs.appearance_visionboard = boardFromProto(preferences.appearanceVisionboard);
  }
  return prefs;
}
